import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  Portal,
  Dialog,
  Button,
  Text,
  useTheme,
  List,
  Chip,
  Searchbar,
  ActivityIndicator,
} from 'react-native-paper';
import { View, Platform, FlatList } from 'react-native';

export interface AssignmentOption {
  id: number;
  title: string;
  description?: string;
  icon?: string;
}

export interface AssignmentFetchResult {
  items: AssignmentOption[];
  totalPages: number;
}

export type AssignmentFetchFn = (
  page: number,
  pageSize: number,
  search?: string
) => Promise<AssignmentFetchResult>;

interface AssignmentSelectionModalProps {
  visible: boolean;
  onDismiss: () => void;
  onAssign: (ids: number[]) => Promise<void>;
  assignedIds: number[];
  fetchOptions: AssignmentFetchFn;
  title: string;
  searchPlaceholder: string;
  emptySearchText: string;
  emptyListText: string;
  selectLabel: string;
  selectedLabel: string;
  selectedCountLabel: (count: number) => string;
  assignButtonLabel: string;
  cancelButtonLabel: string;
  loadErrorMessage: string;
  selectionRequiredMessage: string;
  listTestId?: string;
  debounceDelay?: number;
  pageSize?: number;
}

const DEFAULT_DEBOUNCE = 300;
const DEFAULT_PAGE_SIZE = 20;

export const AssignmentSelectionModal: React.FC<
  AssignmentSelectionModalProps
> = ({
  visible,
  onDismiss,
  onAssign,
  assignedIds,
  fetchOptions,
  title,
  searchPlaceholder,
  emptySearchText,
  emptyListText,
  selectLabel,
  selectedLabel,
  selectedCountLabel,
  assignButtonLabel,
  cancelButtonLabel,
  loadErrorMessage,
  selectionRequiredMessage,
  listTestId,
  debounceDelay = DEFAULT_DEBOUNCE,
  pageSize = DEFAULT_PAGE_SIZE,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [options, setOptions] = useState<AssignmentOption[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, debounceDelay);

    return () => clearTimeout(handler);
  }, [searchQuery, debounceDelay]);

  const resetSelection = useCallback(() => {
    setSelectedIds([]);
    setAssignError(null);
  }, []);

  const assignedIdSet = useMemo(() => new Set(assignedIds), [assignedIds]);

  const availableOptions = useMemo(
    () => options.filter((option) => !assignedIdSet.has(option.id)),
    [options, assignedIdSet]
  );

  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => availableOptions.some((option) => option.id === id))
    );
  }, [availableOptions]);

  const fetchPage = useCallback(
    async (pageToLoad: number, replace: boolean = false) => {
      const currentFetchId = ++fetchIdRef.current;
      setLoading(true);
      setFetchError(null);

      try {
        const { items, totalPages: nextTotalPages } = await fetchOptions(
          pageToLoad,
          pageSize,
          debouncedSearchQuery || undefined
        );

        if (currentFetchId !== fetchIdRef.current) {
          return;
        }

        setOptions((prev) => {
          if (replace) {
            return items;
          }
          const existingIds = new Set(prev.map((option) => option.id));
          const newItems = items.filter((option) => !existingIds.has(option.id));
          return [...prev, ...newItems];
        });

        setPage(pageToLoad);
        setTotalPages(Math.max(1, nextTotalPages || 1));
      } catch (err: any) {
        if (currentFetchId !== fetchIdRef.current) {
          return;
        }
        setFetchError(err?.message || loadErrorMessage);
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setLoading(false);
        }
      }
    },
    [debouncedSearchQuery, fetchOptions, loadErrorMessage, pageSize]
  );

  useEffect(() => {
    if (!visible) {
      setOptions([]);
      setPage(1);
      setTotalPages(1);
      setSearchQuery('');
      setDebouncedSearchQuery('');
      setFetchError(null);
      resetSelection();
      return;
    }

    fetchPage(1, true);
  }, [visible, debouncedSearchQuery, fetchPage, resetSelection]);

  const toggleSelection = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    );
  };

  const handleAssign = async () => {
    if (selectedIds.length === 0) {
      setAssignError(selectionRequiredMessage);
      return;
    }

    setAssigning(true);
    setAssignError(null);

    try {
      await onAssign(selectedIds);
      resetSelection();
      setSearchQuery('');
      onDismiss();
    } catch (err: any) {
      setAssignError(err?.message || loadErrorMessage);
    } finally {
      setAssigning(false);
    }
  };

  const handleDismiss = () => {
    resetSelection();
    setSearchQuery('');
    setFetchError(null);
    onDismiss();
  };

  const handleLoadMore = () => {
    if (loading || page >= totalPages) return;
    fetchPage(page + 1);
  };

  const showNoResults =
    !loading && availableOptions.length === 0 && !fetchError;

  const renderItem = useCallback(
    ({ item }: { item: AssignmentOption }) => {
      const isSelected = selectedIds.includes(item.id);

      return (
        <List.Item
          title={item.title}
          description={item.description}
          left={(props) => (
            <List.Icon
              {...props}
              icon={isSelected ? 'check-circle' : item.icon || 'circle-outline'}
              color={
                isSelected
                  ? theme.colors.primary
                  : theme.colors.onSurfaceVariant
              }
            />
          )}
          right={() => (
            <View
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                marginRight: 8,
              }}
            >
              <Chip
                mode={isSelected ? 'flat' : 'outlined'}
                compact
                onPress={() => toggleSelection(item.id)}
              >
                {isSelected ? selectedLabel : selectLabel}
              </Chip>
            </View>
          )}
          onPress={() => toggleSelection(item.id)}
          style={{
            backgroundColor: isSelected
              ? theme.colors.primaryContainer
              : 'transparent',
          }}
        />
      );
    },
    [
      selectedIds,
      selectLabel,
      selectedLabel,
      theme.colors.onSurfaceVariant,
      theme.colors.primary,
      theme.colors.primaryContainer,
    ]
  );

  return (
    <Portal>
      <Dialog
        visible={visible}
        onDismiss={handleDismiss}
        style={
          Platform.OS === 'web'
            ? {
                maxWidth: 600,
                width: '90%',
                alignSelf: 'center',
              }
            : {}
        }
      >
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.Content>
          <Searchbar
            placeholder={searchPlaceholder}
            onChangeText={setSearchQuery}
            value={searchQuery}
            style={{ marginBottom: 16 }}
            elevation={0}
          />

          {selectedIds.length > 0 && (
            <View style={{ marginBottom: 12 }}>
              <Chip icon="check-circle" mode="flat">
                {selectedCountLabel(selectedIds.length)}
              </Chip>
            </View>
          )}

          {assignError && (
            <Text style={{ color: theme.colors.error, marginBottom: 12 }}>
              {assignError}
            </Text>
          )}
          {fetchError && (
            <Text style={{ color: theme.colors.error, marginBottom: 12 }}>
              {fetchError}
            </Text>
          )}

          {loading && options.length === 0 ? (
            <View style={{ alignItems: 'center', padding: 20 }}>
              <ActivityIndicator />
            </View>
          ) : showNoResults ? (
            <Text
              style={{
                color: theme.colors.onSurfaceVariant,
                textAlign: 'center',
                padding: 20,
              }}
            >
              {searchQuery ? emptySearchText : emptyListText}
            </Text>
          ) : (
            <FlatList
              data={availableOptions}
              keyExtractor={(item) => item.id.toString()}
              renderItem={renderItem}
              onEndReached={handleLoadMore}
              onEndReachedThreshold={0.6}
              contentContainerStyle={{ paddingBottom: 8 }}
              style={{ maxHeight: 400 }}
              keyboardShouldPersistTaps="handled"
              testID={listTestId}
              ListFooterComponent={
                loading ? (
                  <View style={{ alignItems: 'center', paddingVertical: 8 }}>
                    <ActivityIndicator />
                  </View>
                ) : null
              }
            />
          )}
        </Dialog.Content>
        <Dialog.Actions>
          <Button onPress={handleDismiss} disabled={assigning}>
            {cancelButtonLabel}
          </Button>
          <Button
            mode="contained"
            onPress={handleAssign}
            loading={assigning}
            disabled={assigning || selectedIds.length === 0}
          >
            {assignButtonLabel}
            {selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
};


